import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', text: '#E8EDF5', sub: '#C5CDD8',
};

const STYLES = `
  .so-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 32px 20px; border-bottom: 1px solid ${C.border};
  }
  .so-title { font-size: 1.35rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .so-subtitle { color: ${C.muted}; font-size: 0.82rem; margin-top: 4px; }
  .so-btn-primary {
    display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px;
    background: ${C.gold}; color: #0B0E17; border: none; border-radius: 10px;
    font-size: 0.82rem; font-weight: 700; cursor: pointer; transition: opacity .15s;
  }
  .so-btn-primary:hover { opacity: .88; }
  .so-btn-primary:disabled { opacity: .5; cursor: not-allowed; }

  .so-body { padding: 24px 32px; }

  .so-filter-row { display: flex; gap: 6px; margin-bottom: 20px; flex-wrap: wrap; }
  .so-filter-chip {
    padding: 6px 14px; border-radius: 20px; border: 1.5px solid ${C.border};
    background: transparent; color: ${C.muted}; font-weight: 600; font-size: 0.8rem;
    cursor: pointer; transition: all .15s;
  }
  .so-filter-chip.active { border-color: ${C.gold}; background: rgba(240,180,41,0.1); color: ${C.gold}; }
  .so-filter-chip:hover:not(.active) { border-color: rgba(240,180,41,0.4); color: ${C.sub}; }

  .so-loading { padding: 48px; text-align: center; color: ${C.muted}; }
  .so-empty { display: flex; flex-direction: column; align-items: center; padding: 56px 24px; gap: 8px; text-align: center; }
  .so-empty-icon { font-size: 2.5rem; }
  .so-empty-title { font-size: 1rem; font-weight: 700; color: ${C.text}; }
  .so-empty-sub { font-size: 0.85rem; color: ${C.muted}; max-width: 320px; }

  .so-table-wrap {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; overflow: hidden;
  }
  .so-table { width: 100%; border-collapse: collapse; }
  .so-table th {
    padding: 10px 16px; background: rgba(255,255,255,0.03); border-bottom: 1px solid ${C.border};
    font-size: 0.68rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase;
    letter-spacing: .06em; text-align: left; white-space: nowrap;
  }
  .so-table td {
    padding: 13px 16px; border-bottom: 1px solid rgba(42,46,69,0.5);
    font-size: 0.83rem; color: ${C.sub}; vertical-align: middle;
  }
  .so-table tbody tr:last-child td { border-bottom: none; }
  .so-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .so-code-btn {
    background: none; border: none; color: ${C.sky}; font-weight: 700; cursor: pointer;
    padding: 0; font-size: 0.88rem; text-decoration: underline; text-underline-offset: 3px;
  }
  .so-meta { font-size: 0.7rem; color: ${C.muted}; margin-top: 2px; }
  .so-mono { font-family: 'JetBrains Mono', monospace; font-weight: 700; color: ${C.text}; }
  .so-badge {
    display: inline-block; padding: 3px 10px; border-radius: 20px;
    font-size: 0.73rem; font-weight: 700; border: 1px solid;
  }
  .so-action-row { display: flex; gap: 6px; flex-wrap: wrap; }
  .so-btn-sm {
    padding: 5px 11px; border-radius: 7px; font-size: 0.74rem; font-weight: 700; cursor: pointer; transition: all .15s;
  }
  .so-btn-edit { background: transparent; border: 1px solid ${C.border}; color: ${C.sub}; }
  .so-btn-edit:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .so-btn-confirm { background: ${C.sky}; border: none; color: #0B0E17; }
  .so-btn-confirm:hover { opacity: .85; }
  .so-btn-fulfil { background: ${C.teal}; border: none; color: #0B0E17; }
  .so-btn-fulfil:hover { opacity: .85; }
  .so-btn-cancel { background: transparent; border: 1px solid rgba(255,107,107,0.35); color: ${C.coral}; }
  .so-btn-cancel:hover { background: rgba(255,107,107,0.08); }
  .so-btn-delete { background: transparent; border: 1px solid rgba(255,107,107,0.35); color: ${C.coral}; }
  .so-btn-delete:hover { background: rgba(255,107,107,0.08); }
  .so-sale-link { font-size: 0.75rem; font-weight: 600; color: ${C.teal}; }

  /* Modals */
  .so-overlay {
    position: fixed; inset: 0; background: rgba(7,9,14,0.75);
    backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center;
    padding: 20px; z-index: 1000;
  }
  .so-modal {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px;
    width: 100%; max-height: 90vh; overflow-y: auto; animation: so-enter .18s ease;
  }
  @keyframes so-enter { from { opacity: 0; transform: translateY(10px) scale(.97); } to { opacity: 1; transform: none; } }
  .so-modal-head {
    display: flex; justify-content: space-between; align-items: center;
    padding: 20px 22px 16px; border-bottom: 1px solid ${C.border};
  }
  .so-modal-title { font-size: 1rem; font-weight: 800; color: ${C.text}; }
  .so-modal-close {
    padding: 6px 12px; background: transparent; border: 1px solid ${C.border};
    border-radius: 8px; color: ${C.muted}; font-size: 0.78rem; cursor: pointer; transition: all .15s;
  }
  .so-modal-close:hover { border-color: ${C.coral}; color: ${C.coral}; }
  .so-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
  .so-modal-foot {
    display: flex; justify-content: flex-end; gap: 10px;
    padding: 14px 22px; border-top: 1px solid ${C.border};
  }
  .so-error {
    padding: 10px 14px; border-radius: 9px;
    background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3);
    color: ${C.coral}; font-size: 0.8rem;
  }
  .so-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 480px) { .so-form-row { grid-template-columns: 1fr; } }
  .so-form-group { display: flex; flex-direction: column; gap: 6px; }
  .so-label { font-size: 0.73rem; font-weight: 600; color: ${C.muted}; }
  .so-input {
    padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.text}; font-size: 0.83rem; outline: none;
    transition: border-color .15s; width: 100%; box-sizing: border-box; font-family: inherit;
  }
  .so-input:focus { border-color: ${C.teal}; }
  select.so-input option { background: ${C.surface}; }
  .so-btn-ghost-sm {
    padding: 5px 12px; background: transparent; border: 1px dashed ${C.border};
    border-radius: 7px; color: ${C.muted}; font-size: 0.75rem; cursor: pointer; transition: all .15s;
  }
  .so-btn-ghost-sm:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .so-btn-outline {
    padding: 8px 16px; background: transparent; border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.sub}; font-size: 0.82rem; font-weight: 600;
    cursor: pointer; transition: all .15s;
  }
  .so-btn-outline:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .so-btn-outline:disabled { opacity: .5; cursor: not-allowed; }
  .so-remove-btn {
    background: none; border: none; color: ${C.coral}; cursor: pointer; font-size: 1rem; padding: 0 4px;
  }
  .so-totals-box {
    padding: 12px 16px; border-radius: 10px;
    background: rgba(255,255,255,0.03); border: 1px solid ${C.border};
    font-size: 0.85rem;
  }
  .so-totals-row { display: flex; justify-content: space-between; padding: 3px 0; }
  .so-total-final {
    display: flex; justify-content: space-between; font-weight: 700;
    font-size: 1rem; border-top: 1px solid ${C.border}; padding-top: 8px; margin-top: 6px;
    color: ${C.text};
  }
  .so-view-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
    margin-bottom: 16px; font-size: 0.85rem;
  }
  .so-view-label { color: ${C.muted}; }
  .so-view-val { font-weight: 600; color: ${C.text}; }
  .so-view-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  .so-view-table th {
    padding: 8px 12px; background: rgba(255,255,255,0.03); border-bottom: 1px solid ${C.border};
    font-size: 0.68rem; color: ${C.muted}; text-transform: uppercase; letter-spacing: .06em; text-align: left;
  }
  .so-view-table td {
    padding: 10px 12px; border-bottom: 1px solid rgba(42,46,69,0.5);
    font-size: 0.82rem; color: ${C.sub};
  }
  .so-view-table tbody tr:last-child td { border-bottom: none; }
  .so-view-totals { text-align: right; font-size: 0.85rem; margin-top: 10px; display: flex; flex-direction: column; gap: 3px; }
  .so-view-notes {
    margin-top: 12px; font-size: 0.82rem; color: ${C.muted};
    border-top: 1px solid ${C.border}; padding-top: 10px;
  }
  .so-fulfil-hint { font-size: 0.85rem; color: ${C.muted}; margin-bottom: 4px; }
  .so-price-mode {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
  }
  .so-price-opt {
    padding: 10px 12px; border-radius: 8px; cursor: pointer; text-align: left; transition: all .15s; border: 2px solid;
  }
  .so-price-opt-title { font-weight: 700; font-size: 0.78rem; }
  .so-price-opt-sub { font-size: 0.65rem; margin-top: 2px; }
`;

const fmt = (n) =>
  `GHS ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS = {
  draft:     { label: 'Draft',     bg: 'rgba(136,146,164,0.1)',  color: '#8892A4', border: 'rgba(136,146,164,0.2)'  },
  confirmed: { label: 'Confirmed', bg: 'rgba(56,189,248,0.12)',  color: '#38BDF8', border: 'rgba(56,189,248,0.3)'   },
  fulfilled: { label: 'Fulfilled', bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1', border: 'rgba(0,201,177,0.3)'    },
  cancelled: { label: 'Cancelled', bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B', border: 'rgba(255,107,107,0.3)'  },
};

const EMPTY_ITEM = { itemId: '', quantity: 1, price: '' };

export default function SalesOrders() {
  const [orders, setOrders]         = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [inventory, setInventory]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal]           = useState(null);
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState({ customerId: '', items: [{ ...EMPTY_ITEM }], notes: '', dueDate: '', discountType: '', discountValue: '' });
  const [fulfillForm, setFulfillForm] = useState({ paymentMethod: 'cash', settlementType: 'paid', amountPaidNow: '' });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, cRes, iRes] = await Promise.all([
        api.get('/orders'),
        api.get('/customers'),
        api.get('/inventory'),
      ]);
      setOrders(oRes.data || []);
      setCustomers(cRes.data || []);
      setInventory(iRes.data?.items || iRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter ? orders.filter((o) => o.status === statusFilter) : orders;
  const itemMap = Object.fromEntries((inventory || []).map((i) => [String(i.id), i]));

  const updateLine = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    if (field === 'itemId' && value) {
      const inv = itemMap[value];
      if (inv) items[idx].price = Number(inv.price);
    }
    setForm({ ...form, items });
  };

  const addLine    = () => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] });
  const removeLine = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const formSubtotal = form.items.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.price) || 0), 0);
  const formDiscount = form.discountType === 'percent'
    ? (formSubtotal * (Number(form.discountValue) || 0)) / 100
    : form.discountType === 'amount'
      ? Math.min(formSubtotal, Number(form.discountValue) || 0)
      : 0;
  const formTotal = Math.max(0, formSubtotal - formDiscount);

  const openCreate = () => {
    setForm({ customerId: '', items: [{ ...EMPTY_ITEM }], notes: '', dueDate: '', discountType: '', discountValue: '' });
    setSelected(null); setError(''); setModal('create');
  };

  const openEdit = (order) => {
    setForm({
      customerId:    order.customerId ? String(order.customerId) : '',
      items:         order.items.map((i) => ({ itemId: String(i.itemId), quantity: i.quantity, price: i.price })),
      notes:         order.notes || '',
      dueDate:       order.dueDate ? order.dueDate.split('T')[0] : '',
      discountType:  order.discountType  || '',
      discountValue: order.discountValue || '',
    });
    setSelected(order); setError(''); setModal('edit');
  };

  const openView   = (order) => { setSelected(order); setModal('view'); };
  const openFulfil = (order) => {
    setSelected(order);
    setFulfillForm({ paymentMethod: 'cash', settlementType: 'paid', amountPaidNow: '' });
    setError(''); setModal('fulfil');
  };
  const closeModal = () => { setModal(null); setError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    const validItems = form.items.filter((i) => i.itemId && Number(i.quantity) > 0);
    if (!validItems.length) { setError('Add at least one item with quantity'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        customerId:    form.customerId || null,
        items:         validItems.map((i) => ({ itemId: Number(i.itemId), quantity: Number(i.quantity), price: Number(i.price) })),
        notes:         form.notes || null,
        dueDate:       form.dueDate || null,
        discountType:  form.discountType || null,
        discountValue: Number(form.discountValue) || 0,
      };
      if (modal === 'create') await api.post('/orders', payload);
      else                    await api.put(`/orders/${selected.id}`, payload);
      await load(); closeModal();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleAction = async (order, action) => {
    try {
      await api.post(`/orders/${order.id}/${action}`);
      await load();
    } catch (e) { alert(e.response?.data?.error || `${action} failed`); }
  };

  const handleFulfil = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = {
        paymentMethod:  fulfillForm.paymentMethod,
        settlementType: fulfillForm.settlementType,
        ...(fulfillForm.settlementType === 'partial' && { amountPaidNow: Number(fulfillForm.amountPaidNow) }),
      };
      await api.post(`/orders/${selected.id}/fulfil`, payload);
      await load(); closeModal();
    } catch (e) { setError(e.response?.data?.error || 'Fulfilment failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (order) => {
    if (!confirm(`Delete order ${order.code}?`)) return;
    try { await api.delete(`/orders/${order.id}`); await load(); }
    catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Layout>
      <style>{STYLES}</style>

      <div className="so-header">
        <div>
          <h1 className="so-title">Sales Orders</h1>
          <p className="so-subtitle">Manage customer orders before fulfilment</p>
        </div>
        <button className="so-btn-primary" onClick={openCreate}>+ New Order</button>
      </div>

      <div className="so-body">
        <div className="so-filter-row">
          {['', 'draft', 'confirmed', 'fulfilled', 'cancelled'].map((s) => (
            <button
              key={s}
              className={`so-filter-chip${statusFilter === s ? ' active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s ? STATUS[s]?.label : 'All'}{s ? ` (${orders.filter((o) => o.status === s).length})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="so-loading">Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="so-empty">
            <div className="so-empty-icon">📋</div>
            <div className="so-empty-title">No orders yet</div>
            <div className="so-empty-sub">Create a sales order to track customer requests before converting them to sales.</div>
            <button className="so-btn-primary" style={{ marginTop: 8 }} onClick={openCreate}>+ New Order</button>
          </div>
        ) : (
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const st = STATUS[o.status] || STATUS.draft;
                  return (
                    <tr key={o.id}>
                      <td>
                        <button className="so-code-btn" onClick={() => openView(o)}>{o.code}</button>
                        <div className="so-meta">{fmtDate(o.createdAt)}</div>
                      </td>
                      <td>{o.customer?.name || <span style={{ color: C.muted }}>Walk-in</span>}</td>
                      <td>{o.items?.length || 0} item{o.items?.length !== 1 ? 's' : ''}</td>
                      <td><span className="so-mono">{fmt(o.total)}</span></td>
                      <td>{fmtDate(o.dueDate)}</td>
                      <td>
                        <span className="so-badge" style={{ background: st.bg, color: st.color, borderColor: st.border }}>
                          {st.label}
                        </span>
                      </td>
                      <td>
                        <div className="so-action-row">
                          {o.status === 'draft' && (
                            <>
                              <button className="so-btn-sm so-btn-edit" onClick={() => openEdit(o)}>Edit</button>
                              <button className="so-btn-sm so-btn-confirm" onClick={() => handleAction(o, 'confirm')}>Confirm</button>
                            </>
                          )}
                          {o.status === 'confirmed' && (
                            <button className="so-btn-sm so-btn-fulfil" onClick={() => openFulfil(o)}>Fulfil</button>
                          )}
                          {o.status === 'fulfilled' && o.sale && (
                            <span className="so-sale-link">Sale #{String(o.sale.id).padStart(4, '0')}</span>
                          )}
                          {['draft', 'confirmed'].includes(o.status) && (
                            <button className="so-btn-sm so-btn-cancel" onClick={() => handleAction(o, 'cancel')}>Cancel</button>
                          )}
                          {['draft', 'cancelled'].includes(o.status) && (
                            <button className="so-btn-sm so-btn-delete" onClick={() => handleDelete(o)}>🗑</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(modal === 'create' || modal === 'edit') && (
        <div className="so-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="so-modal" style={{ maxWidth: 620 }}>
            <div className="so-modal-head">
              <div className="so-modal-title">{modal === 'create' ? 'New Sales Order' : `Edit ${selected?.code}`}</div>
              <button className="so-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="so-modal-body">
                {error && <div className="so-error">{error}</div>}

                <div className="so-form-row">
                  <div className="so-form-group">
                    <label className="so-label">Customer</label>
                    <select className="so-input" value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })}>
                      <option value="">Walk-in / No customer</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="so-form-group">
                    <label className="so-label">Due Date</label>
                    <input type="date" className="so-input" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
                  </div>
                </div>

                <div className="so-form-group">
                  <label className="so-label">Items</label>
                  {form.items.map((line, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px 28px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <select className="so-input" value={line.itemId} onChange={(e) => updateLine(idx, 'itemId', e.target.value)} required>
                        <option value="">Select item…</option>
                        {(inventory || []).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                      </select>
                      <input type="number" min="1" className="so-input" placeholder="Qty" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} required />
                      <input type="number" min="0" step="0.01" className="so-input" placeholder="Price" value={line.price} onChange={(e) => updateLine(idx, 'price', e.target.value)} required />
                      <button type="button" className="so-remove-btn" onClick={() => removeLine(idx)}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="so-btn-ghost-sm" onClick={addLine}>+ Add line</button>
                </div>

                <div className="so-form-row">
                  <div className="so-form-group">
                    <label className="so-label">Discount type</label>
                    <select className="so-input" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                      <option value="">None</option>
                      <option value="percent">Percent (%)</option>
                      <option value="amount">Fixed amount</option>
                    </select>
                  </div>
                  {form.discountType && (
                    <div className="so-form-group">
                      <label className="so-label">Discount value</label>
                      <input type="number" min="0" step="0.01" className="so-input" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} />
                    </div>
                  )}
                </div>

                <div className="so-form-group">
                  <label className="so-label">Notes</label>
                  <textarea className="so-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ resize: 'vertical' }} />
                </div>

                <div className="so-totals-box">
                  <div className="so-totals-row"><span style={{ color: C.muted }}>Subtotal</span><span className="so-mono" style={{ fontSize: '0.82rem' }}>{fmt(formSubtotal)}</span></div>
                  {formDiscount > 0 && (
                    <div className="so-totals-row"><span style={{ color: C.gold }}>Discount</span><span className="so-mono" style={{ fontSize: '0.82rem', color: C.gold }}>− {fmt(formDiscount)}</span></div>
                  )}
                  <div className="so-total-final"><span>Total</span><span className="so-mono">{fmt(formTotal)}</span></div>
                </div>
              </div>
              <div className="so-modal-foot">
                <button type="button" className="so-btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="so-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : modal === 'create' ? 'Create Order' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal === 'view' && selected && (
        <div className="so-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="so-modal" style={{ maxWidth: 520 }}>
            <div className="so-modal-head">
              <div className="so-modal-title">{selected.code}</div>
              <button className="so-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="so-modal-body">
              <div className="so-view-grid">
                <div><span className="so-view-label">Customer: </span><span className="so-view-val">{selected.customer?.name || 'Walk-in'}</span></div>
                <div>
                  <span className="so-view-label">Status: </span>
                  <span className="so-badge" style={{ background: STATUS[selected.status]?.bg, color: STATUS[selected.status]?.color, borderColor: STATUS[selected.status]?.border }}>
                    {STATUS[selected.status]?.label}
                  </span>
                </div>
                <div><span className="so-view-label">Created: </span><span className="so-view-val">{fmtDate(selected.createdAt)}</span></div>
                <div><span className="so-view-label">Due: </span><span className="so-view-val">{fmtDate(selected.dueDate)}</span></div>
                {selected.sale && <div><span className="so-view-label">Sale: </span><span className="so-view-val">#{String(selected.sale.id).padStart(4, '0')}</span></div>}
              </div>

              <table className="so-view-table">
                <thead><tr><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
                <tbody>
                  {selected.items.map((i) => (
                    <tr key={i.id}>
                      <td style={{ color: C.text, fontWeight: 600 }}>{i.item?.name}</td>
                      <td>{i.quantity}{i.unitName ? ` ${i.unitName}` : ''}</td>
                      <td>{fmt(i.price)}</td>
                      <td><span className="so-mono" style={{ fontSize: '0.8rem' }}>{fmt(i.quantity * i.price)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="so-view-totals">
                <span style={{ color: C.muted }}>Subtotal: {fmt(selected.subtotal)}</span>
                {Number(selected.discountAmount) > 0 && <span style={{ color: C.gold }}>Discount: − {fmt(selected.discountAmount)}</span>}
                <span className="so-mono" style={{ fontSize: '1rem' }}>Total: {fmt(selected.total)}</span>
              </div>
              {selected.notes && <div className="so-view-notes">Notes: {selected.notes}</div>}
            </div>
            <div className="so-modal-foot">
              <button className="so-btn-outline" onClick={closeModal}>Close</button>
              {selected.status === 'confirmed' && (
                <button className="so-btn-primary" onClick={() => { closeModal(); openFulfil(selected); }}>Fulfil Order</button>
              )}
            </div>
          </div>
        </div>
      )}

      {modal === 'fulfil' && selected && (
        <div className="so-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="so-modal" style={{ maxWidth: 420 }}>
            <div className="so-modal-head">
              <div className="so-modal-title">Fulfil {selected.code}</div>
              <button className="so-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleFulfil}>
              <div className="so-modal-body">
                {error && <div className="so-error">{error}</div>}
                <p className="so-fulfil-hint">
                  This will create a completed sale for <span className="so-mono" style={{ fontSize: '0.85rem' }}>{fmt(selected.total)}</span> and deduct inventory.
                </p>

                <div className="so-form-group">
                  <label className="so-label">Payment Method</label>
                  <select className="so-input" value={fulfillForm.paymentMethod} onChange={(e) => setFulfillForm({ ...fulfillForm, paymentMethod: e.target.value })}>
                    <option value="cash">Cash</option>
                    <option value="momo">Mobile Money (MoMo)</option>
                    <option value="card">Card</option>
                    <option value="credit">Credit (pay later)</option>
                  </select>
                </div>

                {fulfillForm.paymentMethod !== 'credit' && (
                  <div className="so-form-group">
                    <label className="so-label">Settlement</label>
                    <select className="so-input" value={fulfillForm.settlementType} onChange={(e) => setFulfillForm({ ...fulfillForm, settlementType: e.target.value })}>
                      <option value="paid">Paid in full</option>
                      <option value="partial">Partial payment</option>
                    </select>
                  </div>
                )}

                {fulfillForm.settlementType === 'partial' && (
                  <div className="so-form-group">
                    <label className="so-label">Amount paid now (GHS)</label>
                    <input type="number" min="0" step="0.01" className="so-input" value={fulfillForm.amountPaidNow} onChange={(e) => setFulfillForm({ ...fulfillForm, amountPaidNow: e.target.value })} required />
                  </div>
                )}
              </div>
              <div className="so-modal-foot">
                <button type="button" className="so-btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="so-btn-primary" disabled={saving}>
                  {saving ? 'Processing…' : 'Fulfil Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
